import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  useTheme,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Typography,
  Alert,
  Stack,
  Tooltip,
} from "@mui/material";
import EventsForm, { creatorInitialFormData, type EventsFormData } from "../EventForm/EventFormMain";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEventsApi } from "../../hooks/useEventsApi";
import { config } from "../../config";
import type { NumberRangeBaseFunction, NumberRangeSubFunction } from "../../shared/NumberRange";
import type { FullEvent } from "../../shared/FullEvent";
import {
  computeCodeRangeFromDigits,
  isResolvingBandCode,
  risingToResolvingCode,
} from "../../shared/eventCodeRange";
import { normalizeEventLanguage } from "../../constants/languages";

/** English help for creator “event creation mode” radios (rising vs resolving). */
const TOOLTIP_RISING_AND_RESOLVING =
  "Creates a matched pair: a rising event fires on the rising edge when a condition becomes true (e.g. a safety scanner detects something in the protected zone), and a resolving event fires when that condition clears (e.g. the zone is free again). A resolving event is not always required—you can use Rising only if no clear event is needed.";

const TOOLTIP_RISING_ONLY =
  "Rising events fire on the rising edge: when a condition becomes active—for example, a person safety scanner reports something in the protected field. This is the “something happened” side. A separate resolving event is optional and not always required.";

const TOOLTIP_RESOLVING_ONLY =
  "Resolving events fire when the condition clears or is released—for example, the protected field is free again after an obstacle. They pair with an existing rising event; you must look up that rising code first. Not every rising event needs a resolving event.";

const TOOLTIP_EVENT_CREATION_MODE_OVERVIEW =
  "Rising = condition becomes active (rising edge), e.g. scanner sees something in the zone. Resolving = condition clears, e.g. zone is free again. Resolving is optional; hover each option for details.";

function buildResolvingDraftFromRising(rising: FullEvent, resolvingCode: number): EventsFormData {
  const langs = ["ger", "eng", "ita", "cze"] as const;
  const emptyDescs = langs.map((lang) => ({
    id: 0,
    global_event: 0,
    language_code: lang,
    label: "",
    label_long: "",
    description: "",
    situation: "",
    actions: [""],
  }));
  const params = rising.attributes?.parameters
    ? JSON.parse(JSON.stringify(rising.attributes.parameters))
    : creatorInitialFormData.attributes!.parameters;

  const risingHmiByLang: Record<string, string> = {};
  for (const d of rising.event_descriptions ?? []) {
    const lc = normalizeEventLanguage(d.language_code);
    const lab = (d.label ?? "").trim();
    if (lab) {
      risingHmiByLang[lc] = lab;
    }
  }

  return {
    ...creatorInitialFormData,
    global_event_id: 0,
    event_type: rising.event_type,
    event_contract_name: "",
    event_code: resolvingCode,
    version: 0,
    module: rising.module,
    plant_specific: rising.plant_specific,
    attributes: {
      ...creatorInitialFormData.attributes,
      ...rising.attributes,
      severity: rising.attributes?.severity || "Info",
      event_code_old: resolvingCode,
      creator_source_language: rising.attributes?.creator_source_language || "ger",
      creator_resolving_linked_rising_global_id: rising.global_event_id,
      creator_resolving_rising_hmi_labels: risingHmiByLang,
      creator_resolving_rising_contract_name: rising.event_contract_name ?? "",
      parameters: params,
      alarm: false,
      message_history: false,
    },
    event_descriptions: emptyDescs,
    approved_by: "",
    approved_at: null,
  };
}

export type Step3ConfiguratorPhase = "mode" | "rising" | "resolving";

interface Props {
  /** Which slice of the creator wizard this instance renders. */
  phase: Step3ConfiguratorPhase;
  data: any;
  eventCreation: string;
  onEventCreationChange?: (value: string) => void;
  onValidationChange: (valid: boolean) => void;
  onDataChange: (data: any) => void;
  step1Data?: { selectedBaseFunctionId?: number };
  step2Data?: { selectedSubFunction?: number };
  /** Set from step-1 shortcut: auto-run rising lookup once on the resolving step. */
  quickResolvingRisingCode?: string | null;
  /** Contract name from the rising event (both mode: step3a; resolving-only: linked rising). */
  risingContractName?: string | null;
  onOpenNumberRangeOverview?: (opts?: { lookupCode?: string }) => void;
}

export default function Step3Form({
  phase,
  data,
  eventCreation,
  onEventCreationChange,
  onValidationChange,
  onDataChange,
  step1Data,
  step2Data,
  quickResolvingRisingCode,
  risingContractName,
  onOpenNumberRangeOverview,
}: Props) {
  const theme = useTheme();
  const { callApi } = useEventsApi();

  const [kindOfEventCreation, setKindOfEventCreation] = useState<"none" | "both" | "rising" | "resolving">(
    eventCreation as any
  );

  const [risingCodeInput, setRisingCodeInput] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [innerValid, setInnerValid] = useState(false);
  const [baseFunctions, setBaseFunctions] = useState<NumberRangeBaseFunction[]>([]);
  const [subFunctions, setSubFunctions] = useState<NumberRangeSubFunction[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  useEffect(() => {
    setKindOfEventCreation(eventCreation as "none" | "both" | "rising" | "resolving");
  }, [eventCreation]);

  const handleEventCreationChange = (value: "both" | "rising" | "resolving") => {
    setKindOfEventCreation(value);
    onEventCreationChange?.(value);
    if (value !== "resolving") {
      setLookupError(null);
    }
  };

  useEffect(() => {
    if (kindOfEventCreation !== "resolving") {
      setLoadingMeta(false);
      return;
    }
    const baseId = step1Data?.selectedBaseFunctionId;
    if (!baseId) {
      setLoadingMeta(false);
      return;
    }
    let cancelled = false;
    setLoadingMeta(true);
    void (async () => {
      try {
        const [bRes, sRes] = await Promise.all([
          callApi(`${config.apiUrl}/api/number-range-base-function/`, "GET"),
          callApi(`${config.apiUrl}/api/number-range-sub-function/?base=${baseId}`, "GET"),
        ]);
        if (cancelled) return;
        setBaseFunctions(bRes?.results ?? []);
        setSubFunctions(sRes?.results ?? []);
      } catch {
        if (!cancelled) {
          setBaseFunctions([]);
          setSubFunctions([]);
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callApi identity is unstable; values above are sufficient
  }, [kindOfEventCreation, step1Data?.selectedBaseFunctionId]);

  const risingLinked = Boolean(data?.attributes?.creator_resolving_linked_rising_global_id);

  useEffect(() => {
    const id = data?.attributes?.creator_resolving_linked_rising_global_id;
    if (id && typeof data.event_code === "number" && isResolvingBandCode(data.event_code)) {
      setRisingCodeInput(String(data.event_code - 10000));
    }
  }, [data?.attributes?.creator_resolving_linked_rising_global_id, data?.event_code]);

  const performRisingLookup = useCallback(
    async (opts: { codeInput: string; skipRangeCheck?: boolean }) => {
      setLookupError(null);
      const raw = opts.codeInput.trim();
      const risingCode = parseInt(raw, 10);
      if (Number.isNaN(risingCode)) {
        setLookupError("Enter a numeric rising event code.");
        return;
      }
      if (isResolvingBandCode(risingCode)) {
        setLookupError("That code is in the resolving range (1xxxx). Enter the rising event code (0xxxx).");
        return;
      }

      if (!opts.skipRangeCheck) {
        const baseId = step1Data?.selectedBaseFunctionId;
        const subId = step2Data?.selectedSubFunction;
        const baseDigit = baseFunctions.find((b) => b.id === baseId)?.digit;
        const subDigit = subFunctions.find((s) => s.id === subId)?.digit;
        if (baseDigit === undefined || subDigit === undefined) {
          setLookupError("Could not resolve base/sub digits. Go back and select base and sub functions.");
          return;
        }

        const { min, max } = computeCodeRangeFromDigits(baseDigit, subDigit);
        if (risingCode < min || risingCode > max) {
          setLookupError(`Rising code must be within ${min}–${max} for the selected base/sub functions.`);
          return;
        }
      }

      setLookupBusy(true);
      try {
        const listUrl = `${config.apiUrl}/api/events/?event_code_exact=${risingCode}&latest=true&page_size=5`;
        const res = await callApi(listUrl, "GET");
        const row = res?.results?.[0];
        if (!row) {
          setLookupError(`No event with code ${risingCode} was found.`);
          return;
        }

        const full = (await callApi(`${config.apiUrl}/api/events/${row.global_event_id}/`, "GET")) as FullEvent;

        if (isResolvingBandCode(full.event_code)) {
          setLookupError("This code belongs to a resolving event, not a rising event.");
          return;
        }

        const resolvingCode = risingToResolvingCode(full.event_code);
        const dupUrl = `${config.apiUrl}/api/events/?event_code_exact=${resolvingCode}&latest=true&page_size=5`;
        const dupRes = await callApi(dupUrl, "GET");
        if (dupRes?.results?.length > 0) {
          setLookupError(
            `Resolving event ${resolvingCode} already exists (event id ${dupRes.results[0].global_event_id}).`
          );
          return;
        }

        const draft = buildResolvingDraftFromRising(full, resolvingCode);
        onDataChange(draft);
        setRisingCodeInput(String(full.event_code));
      } catch (e) {
        console.error(e);
        setLookupError("Lookup failed. Check permissions and try again.");
      } finally {
        setLookupBusy(false);
      }
    },
    [
      step1Data?.selectedBaseFunctionId,
      step2Data?.selectedSubFunction,
      baseFunctions,
      subFunctions,
      callApi,
      onDataChange,
    ]
  );

  useEffect(() => {
    if (!quickResolvingRisingCode) return;
    if (kindOfEventCreation !== "resolving" || phase !== "resolving") return;
    void performRisingLookup({ codeInput: quickResolvingRisingCode, skipRangeCheck: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- performRisingLookup identity changes often; quick path is one-shot per prop value
  }, [quickResolvingRisingCode, kindOfEventCreation, phase]);

  const handleLookupRising = useCallback(() => {
    void performRisingLookup({ codeInput: risingCodeInput });
  }, [performRisingLookup, risingCodeInput]);

  const handleClearRisingLink = useCallback(() => {
    setLookupError(null);
    setInnerValid(false);
    setRisingCodeInput("");
    onDataChange(JSON.parse(JSON.stringify(creatorInitialFormData)) as EventsFormData);
  }, [onDataChange]);

  useEffect(() => {
    if (phase === "mode") {
      onValidationChange(kindOfEventCreation !== "none");
      return;
    }
    if (phase === "resolving" && kindOfEventCreation === "resolving") {
      onValidationChange(risingLinked && innerValid);
    }
  }, [phase, kindOfEventCreation, risingLinked, innerValid, onValidationChange]);

  const onResolvingEventsFormValid = useCallback(
    (v: boolean) => {
      if (kindOfEventCreation === "resolving" && phase === "resolving") {
        setInnerValid(v);
      } else {
        onValidationChange(v);
      }
    },
    [kindOfEventCreation, phase, onValidationChange]
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 950 }}>
        {phase === "mode" && (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <FormControl
              component="fieldset"
              size="small"
              sx={{
                mb: 2,
                flexWrap: "wrap",
                alignItems: "left",
                border: "1px solid",
                borderColor: theme.palette.divider,
                borderRadius: 2,
                p: 2,
                bgcolor: "background.paper",
                width: { xs: "100%", md: "70%" },
                boxSizing: "border-box",
              }}
            >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
              <FormLabel sx={{ color: theme.palette.text.primary, mb: 0 }}>Event creation mode</FormLabel>
              <Tooltip
                title={TOOLTIP_EVENT_CREATION_MODE_OVERVIEW}
                enterDelay={300}
                disableInteractive
                slotProps={{
                  popper: {
                    sx: { pointerEvents: "none" },
                  },
                }}
              >
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ cursor: "help", color: "action.active", verticalAlign: "middle" }}
                  aria-label="What are rising and resolving events?"
                />
              </Tooltip>
            </Box>

            <RadioGroup
              row
              value={kindOfEventCreation === "none" ? "" : kindOfEventCreation}
              onChange={(e) => handleEventCreationChange(e.target.value as "both" | "rising" | "resolving")}
            >
              <Tooltip
                title={TOOLTIP_RISING_AND_RESOLVING}
                enterDelay={400}
                placement="top"
                disableInteractive
                slotProps={{
                  popper: {
                    sx: { pointerEvents: "none" },
                  },
                }}
              >
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <FormControlLabel
                    sx={{ color: theme.palette.text.primary }}
                    value="both"
                    control={<Radio size="small" />}
                    label="Rising + resolving"
                  />
                </Box>
              </Tooltip>
              <Tooltip
                title={TOOLTIP_RISING_ONLY}
                enterDelay={400}
                placement="top"
                disableInteractive
                slotProps={{
                  popper: {
                    sx: { pointerEvents: "none" },
                  },
                }}
              >
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <FormControlLabel
                    sx={{ color: theme.palette.text.primary }}
                    value="rising"
                    control={<Radio size="small" />}
                    label="Rising only"
                  />
                </Box>
              </Tooltip>
              <Tooltip
                title={TOOLTIP_RESOLVING_ONLY}
                enterDelay={400}
                placement="top"
                disableInteractive
                slotProps={{
                  popper: {
                    sx: { pointerEvents: "none" },
                  },
                }}
              >
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <FormControlLabel
                    sx={{ color: theme.palette.text.primary }}
                    value="resolving"
                    control={<Radio size="small" />}
                    label="Resolving only (requires existing rising event)"
                  />
                </Box>
              </Tooltip>
            </RadioGroup>
            </FormControl>
          </Box>
        )}

        {phase === "rising" && kindOfEventCreation !== "resolving" && kindOfEventCreation !== "none" && (
          <Box mt={2}>
            <EventsForm
              createNewEvent={true}
              configuratorMode={true}
              data={data}
              isFallingEvent={false}
              onDataChange={onDataChange}
              onValidChange={onValidationChange}
            />
          </Box>
        )}

        {phase === "resolving" && kindOfEventCreation === "resolving" && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, width: "100%" }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: theme.palette.text.primary }}>
              Rising event (required)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
              Enter the five-digit rising event code (0xxxx band). The resolving event will use the same numeric suffix
              with a leading 1 (e.g. rising 256 → resolving 10256).
            </Typography>
            {loadingMeta ? (
              <CircularProgress size={28} />
            ) : (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" flexWrap="wrap">
                <TextField
                  label="Rising event code"
                  value={risingCodeInput}
                  onChange={(e) => setRisingCodeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !lookupBusy && !risingLinked) {
                      e.preventDefault();
                      void handleLookupRising();
                    }
                  }}
                  disabled={lookupBusy || risingLinked}
                  size="small"
                  sx={{ minWidth: 200 }}
                />
                <Button variant="contained" onClick={() => void handleLookupRising()} disabled={lookupBusy || risingLinked}>
                  {lookupBusy ? <CircularProgress size={22} color="inherit" /> : "Lookup & link"}
                </Button>
                {risingLinked && (
                  <Button variant="outlined" color="warning" onClick={handleClearRisingLink}>
                    Change rising event
                  </Button>
                )}
              </Stack>
            )}
            {lookupError && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                action={
                  onOpenNumberRangeOverview &&
                  /within \d+–\d+/i.test(lookupError) ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        onOpenNumberRangeOverview({
                          lookupCode: risingCodeInput.trim() || undefined,
                        })
                      }
                    >
                      Show expected range
                    </Button>
                  ) : undefined
                }
              >
                {lookupError}
              </Alert>
            )}
            {risingLinked && typeof data?.event_code === "number" && isResolvingBandCode(data.event_code) && (
              <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
                Resolving event code: <b>{data.event_code}</b> (pair of rising {data.event_code - 10000})
              </Typography>
            )}
          </Paper>
        )}

        {phase === "resolving" &&
          kindOfEventCreation !== "rising" &&
          kindOfEventCreation !== "none" &&
          (kindOfEventCreation !== "resolving" || risingLinked) && (
            <Box mt={2}>
              <EventsForm
                key={String(data?.attributes?.creator_resolving_linked_rising_global_id ?? "unlinked")}
                createNewEvent={true}
                configuratorMode={true}
                data={data}
                isFallingEvent={true}
                creatorRisingContractName={risingContractName}
                onDataChange={onDataChange}
                onValidChange={onResolvingEventsFormValid}
              />
            </Box>
          )}
      </Box>
    </Box>
  );
}
